import React from 'react';

/**
 * BuildPilot Callout Primitives (2026-02-19)
 * ──────────────────────────────────────────────────────────────
 * Four reusable blocks that replace every pastel callout site-wide.
 * Brand-locked: only colours that live in the BuildPilot logo are
 * used (navy / blue / cyan / white / lavender-white).
 *
 * Use ONE of these - do not invent a fifth. Adding new colour
 * variants will be flagged in code review.
 *
 * Surfaces map directly to the tokens in `index.css` and the rule
 * in `BUILD_TEMPLATE.md §15.3` + §15.3a.
 * ──────────────────────────────────────────────────────────────
 */

/* ───────────────────────────────────────────────────────────────
 * <EditorialTip>
 * For quick answers, helpful tips, "did you know" facts, FAQ blocks.
 * White background + 3px brand-gradient left bar + blue eyebrow.
 * Reads like a New Yorker pull-quote with the BuildPilot brand bar.
 * ─────────────────────────────────────────────────────────────── */
export const EditorialTip = ({ eyebrow = 'Quick answer', title, children, icon: Icon, className = '', testId = 'editorial-tip', ...rest }) => (
  <aside
    data-testid={testId}
    className={`relative bg-white rounded-r-xl pl-5 pr-5 py-5 sm:pl-6 sm:pr-6 sm:py-6 my-6 ${className}`}
    style={{
      borderLeft: '3px solid transparent',
      backgroundImage: 'linear-gradient(white, white), linear-gradient(180deg, #0a2540 0%, #1d8efe 50%, #4abff2 100%)',
      backgroundOrigin: 'border-box',
      backgroundClip: 'padding-box, border-box',
    }}
    {...rest}
  >
    <div className="flex items-start gap-3">
      {Icon ? (
        <Icon className="w-[18px] h-[18px] mt-0.5 flex-shrink-0" style={{ color: 'var(--bp-color-blue)' }} />
      ) : null}
      <div className="flex-1 min-w-0">
        {eyebrow ? (
          <p
            className="text-[10px] sm:text-[11px] font-semibold uppercase mb-1.5"
            style={{ color: 'var(--bp-color-blue)', letterSpacing: '0.18em' }}
            data-testid={`${testId}-eyebrow`}
          >
            {eyebrow}
          </p>
        ) : null}
        {title ? (
          <h3 className="text-base sm:text-lg font-semibold mb-1.5" style={{ color: 'var(--bp-color-ink)' }}>
            {title}
          </h3>
        ) : null}
        <div className="text-[15px] sm:text-base leading-[1.6]" style={{ color: 'var(--bp-color-ink)' }}>
          {children}
        </div>
      </div>
    </div>
  </aside>
);

/* ───────────────────────────────────────────────────────────────
 * <TintedPanel>
 * Soft section grouping. The lavender-white panel from the logo
 * collage - instantly reads as "part of the BuildPilot brand".
 * Use to group related cards / facts / lists without adding a card-
 * on-card visual. No border, generous radius and padding.
 * ─────────────────────────────────────────────────────────────── */
export const TintedPanel = ({ children, className = '', testId = 'tinted-panel', ...rest }) => (
  <div
    data-testid={testId}
    className={`rounded-2xl p-6 sm:p-8 ${className}`}
    style={{ background: 'var(--bp-color-surface-tinted)' }}
    {...rest}
  >
    {children}
  </div>
);

/* ───────────────────────────────────────────────────────────────
 * <PremiumNote>
 * Dark navy block with white text and a cyan eyebrow. Use for:
 *  - One on One Support / paid features
 *  - Featured partner / advertiser content
 *  - Premium opportunities, exclusive H&L
 * Mirrors the dark hero treatment in the logo collage.
 * ─────────────────────────────────────────────────────────────── */
export const PremiumNote = ({ eyebrow = 'Premium', title, children, className = '', testId = 'premium-note', ...rest }) => (
  <div
    data-testid={testId}
    className={`rounded-2xl p-6 sm:p-8 ${className}`}
    style={{ background: 'var(--bp-color-surface-premium)', color: '#ffffff' }}
    {...rest}
  >
    {eyebrow ? (
      <p
        className="text-[10px] sm:text-[11px] font-semibold uppercase mb-2"
        style={{ color: 'var(--bp-color-cyan)', letterSpacing: '0.2em' }}
        data-testid={`${testId}-eyebrow`}
      >
        {eyebrow}
      </p>
    ) : null}
    {title ? (
      <h3 className="text-lg sm:text-xl font-semibold tracking-tight mb-2 text-white">{title}</h3>
    ) : null}
    <div className="text-[14px] sm:text-[15px] leading-[1.6]" style={{ color: 'rgba(255,255,255,0.78)' }}>
      {children}
    </div>
  </div>
);

/* ───────────────────────────────────────────────────────────────
 * <IntelligenceNote>
 * For AI / CoPilot / Intelligence-powered surfaces. Subtle violet
 * eyebrow + AI-gradient left bar (navy → blue → violet). The
 * "intelligent" signal - used to mark blocks where BuildPilot
 * Intelligence is actively doing work for the user (matching,
 * scoring, reasoning, summarising, predicting).
 *
 * Usage rule: never as a solid fill. The violet only ever lives
 * on the eyebrow text and the gradient bar terminus.
 * ─────────────────────────────────────────────────────────────── */
export const IntelligenceNote = ({ eyebrow = 'BuildPilot Intelligence', title, children, icon: Icon, className = '', testId = 'intelligence-note', ...rest }) => (
  <aside
    data-testid={testId}
    className={`relative bg-white rounded-r-xl pl-5 pr-5 py-5 sm:pl-6 sm:pr-6 sm:py-6 my-6 ${className}`}
    style={{
      borderLeft: '3px solid transparent',
      backgroundImage: 'linear-gradient(white, white), linear-gradient(180deg, #0a2540 0%, #1d8efe 45%, #7c3aed 100%)',
      backgroundOrigin: 'border-box',
      backgroundClip: 'padding-box, border-box',
    }}
    {...rest}
  >
    <div className="flex items-start gap-3">
      {Icon ? (
        <Icon className="w-[18px] h-[18px] mt-0.5 flex-shrink-0" style={{ color: 'var(--bp-color-intelligence)' }} />
      ) : null}
      <div className="flex-1 min-w-0">
        {eyebrow ? (
          <p
            className="text-[10px] sm:text-[11px] font-semibold uppercase mb-1.5"
            style={{ color: 'var(--bp-color-intelligence)', letterSpacing: '0.18em' }}
            data-testid={`${testId}-eyebrow`}
          >
            {eyebrow}
          </p>
        ) : null}
        {title ? (
          <h3 className="text-base sm:text-lg font-semibold mb-1.5" style={{ color: 'var(--bp-color-ink)' }}>
            {title}
          </h3>
        ) : null}
        <div className="text-[15px] sm:text-base leading-[1.6]" style={{ color: 'var(--bp-color-ink)' }}>
          {children}
        </div>
      </div>
    </div>
  </aside>
);

/* ───────────────────────────────────────────────────────────────
 * <Disclosure>
 * Legal / publisher / introducer footnote. No background, no
 * border around it - just a 1px hairline top divider and italic
 * small text in the quiet grey. Reads like a serious newspaper
 * sign-off, not a Tailwind tutorial.
 * ─────────────────────────────────────────────────────────────── */
export const Disclosure = ({ children, prefix = '-', className = '', testId = 'disclosure', ...rest }) => (
  <p
    data-testid={testId}
    className={`mt-6 pt-3 text-[11px] sm:text-[12px] italic leading-[1.65] ${className}`}
    style={{
      borderTop: '1px solid var(--bp-color-hairline)',
      color: 'var(--bp-color-quiet)',
    }}
    {...rest}
  >
    {prefix ? <span aria-hidden="true" style={{ marginRight: 6 }}>{prefix}</span> : null}
    {children}
  </p>
);

export default EditorialTip;

/* ───────────────────────────────────────────────────────────────
 * <StatTile>
 * Big figure callout - drop one every 600-800 words to anchor a
 * key number visually. The figure dominates (48px), the eyebrow
 * is tiny brand-blue, and the caption sits underneath in quiet
 * grey. Reads as a magazine sidebar pulled from the prose.
 * ─────────────────────────────────────────────────────────────── */
export const StatTile = ({ value, eyebrow, caption, className = '', testId = 'stat-tile', ...rest }) => (
  <div
    data-testid={testId}
    className={`my-8 inline-block ${className}`}
    style={{
      padding: '20px 24px',
      borderRadius: 16,
      background: 'var(--bp-color-surface-tinted)',
      minWidth: 220,
    }}
    {...rest}
  >
    {eyebrow ? (
      <p
        className="text-[10px] sm:text-[11px] font-semibold uppercase mb-1.5"
        style={{ color: 'var(--bp-color-blue)', letterSpacing: '0.18em' }}
        data-testid={`${testId}-eyebrow`}
      >
        {eyebrow}
      </p>
    ) : null}
    <div
      className="font-bold leading-[1] tracking-[-0.02em] mb-1.5"
      style={{
        fontSize: 'clamp(36px, 6vw, 48px)',
        color: 'var(--bp-color-ink)',
      }}
      data-testid={`${testId}-value`}
    >
      {value}
    </div>
    {caption ? (
      <p
        className="text-[12px] sm:text-[13px] leading-[1.5]"
        style={{ color: 'var(--bp-color-quiet)' }}
      >
        {caption}
      </p>
    ) : null}
  </div>
);

/* ───────────────────────────────────────────────────────────────
 * <PullQuote>
 * Mid-article serif pull-quote with a vertical brand-gradient
 * rule. Two per long article elevate the perceived editorial
 * quality dramatically. Always source the quote (a real human
 * name + face) - anonymous pull-quotes feel AI-generated.
 *
 * `attribution` accepts either:
 *   • a string (legacy)  - renders "- NAME" caption.
 *   • an object { name, role, image } - renders avatar + name + role
 *     so the quote reads as a sourced editorial pull-quote, not an
 *     anonymous AI paraphrase.
 * ─────────────────────────────────────────────────────────────── */
export const PullQuote = ({ children, attribution, className = '', testId = 'pull-quote', ...rest }) => {
  const isObj = attribution && typeof attribution === 'object';
  return (
    <figure
      data-testid={testId}
      className={`my-10 pl-5 sm:pl-6 ${className}`}
      style={{
        borderLeft: '3px solid transparent',
        backgroundImage: 'linear-gradient(white, white), linear-gradient(180deg, #0a2540 0%, #1d8efe 50%, #4abff2 100%)',
        backgroundOrigin: 'border-box',
        backgroundClip: 'padding-box, border-box',
      }}
      {...rest}
    >
      <blockquote
        className="text-[20px] sm:text-[22px] lg:text-[24px] leading-[1.45] tracking-[-0.01em] font-medium"
        style={{ color: 'var(--bp-color-ink)', fontFamily: 'Georgia, "Times New Roman", serif' }}
      >
        &ldquo;{children}&rdquo;
      </blockquote>
      {isObj ? (
        <figcaption
          className="mt-4 flex items-center gap-3"
          data-testid={`${testId}-attribution`}
        >
          {attribution.image ? (
            <img
              src={attribution.image}
              alt={attribution.name || ''}
              className="w-9 h-9 sm:w-10 sm:h-10 rounded-full object-cover border border-gray-200 flex-shrink-0"
              loading="lazy"
              decoding="async"
            />
          ) : null}
          <div className="min-w-0">
            <p className="text-[13px] sm:text-[14px] font-semibold m-0 leading-tight" style={{ color: 'var(--bp-color-ink)' }}>
              {attribution.name}
            </p>
            {attribution.role ? (
              <p className="text-[11px] sm:text-[12px] m-0 leading-tight mt-0.5" style={{ color: 'var(--bp-color-quiet)' }}>
                {attribution.role}
              </p>
            ) : null}
          </div>
        </figcaption>
      ) : attribution ? (
        <figcaption
          className="mt-3 text-[12px] sm:text-[13px] font-medium uppercase"
          style={{ color: 'var(--bp-color-blue)', letterSpacing: '0.14em' }}
          data-testid={`${testId}-attribution`}
        >
          - {attribution}
        </figcaption>
      ) : null}
    </figure>
  );
};

/* ───────────────────────────────────────────────────────────────
 * <ComparisonTable>
 * Replaces 4-6 paragraphs of "X vs Y" prose with a scannable
 * 2-column layout. Accepts a header row + body rows.
 *
 * <ComparisonTable
 *   headers={['Volume builder', 'Custom builder']}
 *   rows={[
 *     { label: 'Typical cost', values: ['$1,800/m²', '$3,200/m²'] },
 *     ...
 *   ]}
 * />
 * ─────────────────────────────────────────────────────────────── */
export const ComparisonTable = ({ headers = [], rows = [], className = '', testId = 'comparison-table', ...rest }) => (
  <div
    data-testid={testId}
    className={`my-10 overflow-hidden rounded-2xl ${className}`}
    style={{ background: 'var(--bp-color-surface-tinted)' }}
    {...rest}
  >
    <table className="w-full border-collapse text-left">
      <thead>
        <tr style={{ borderBottom: '1px solid var(--bp-color-hairline)' }}>
          <th
            className="px-5 sm:px-6 py-3 text-[10px] sm:text-[11px] font-semibold uppercase"
            style={{ color: 'var(--bp-color-quiet)', letterSpacing: '0.14em' }}
          />
          {headers.map((h, i) => (
            <th
              key={i}
              className="px-5 sm:px-6 py-3 text-[10px] sm:text-[11px] font-semibold uppercase"
              style={{ color: 'var(--bp-color-blue)', letterSpacing: '0.14em' }}
              data-testid={`${testId}-header-${i}`}
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, ri) => (
          <tr
            key={ri}
            style={{ borderTop: ri > 0 ? '1px solid var(--bp-color-hairline)' : 'none' }}
          >
            <td
              className="px-5 sm:px-6 py-3.5 text-[13px] sm:text-[14px] font-semibold align-top"
              style={{ color: 'var(--bp-color-ink)' }}
            >
              {row.label}
            </td>
            {row.values.map((v, vi) => (
              <td
                key={vi}
                className="px-5 sm:px-6 py-3.5 text-[13px] sm:text-[14px] align-top leading-[1.55]"
                style={{ color: 'var(--bp-color-ink)' }}
                data-testid={`${testId}-cell-${ri}-${vi}`}
              >
                {v}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
